import {
  Box,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from '@chakra-ui/react';
import { STORAGE_QUOTA_EVENT } from 'hooks/useCodeStorage';
import { CodeVersion, useCodeVersionHistory } from 'hooks/useCodeVersionHistory';
import { useEffect, useState } from 'react';

import { useDevSandbox } from './DevSandboxProvider';
import useSandboxTheme from './useSandboxTheme';

type SaveStatus = 'saved' | 'saving' | 'error';

/**
 * Minimal save status indicator for footer center.
 * Shows a subtle dot + text with optional history dropdown.
 */
const SaveIndicator = () => {
  const {
    id,
    codeTabs: { html, css, js },
    handlers: { onHtmlChange, onCssChange, onJsChange },
  } = useDevSandbox();

  const htmlCode = html ?? '';
  const cssCode = css ?? '';
  const jsCode = js ?? '';

  const [status, setStatus] = useState<SaveStatus>('saved');
  const [lastCode, setLastCode] = useState({ html: htmlCode, css: cssCode, js: jsCode });

  const { versions, restoreVersion, getRelativeTime } = useCodeVersionHistory({
    storageKey: id,
    html: htmlCode,
    css: cssCode,
    js: jsCode,
  });

  useEffect(() => {
    if (htmlCode !== lastCode.html || cssCode !== lastCode.css || jsCode !== lastCode.js) {
      setStatus('saving');
      const timeoutId = setTimeout(() => {
        setLastCode({ html: htmlCode, css: cssCode, js: jsCode });
        setStatus('saved');
      }, 600);
      return () => clearTimeout(timeoutId);
    }
  }, [htmlCode, cssCode, jsCode, lastCode]);

  useEffect(() => {
    const handler = () => setStatus('error');
    window.addEventListener(STORAGE_QUOTA_EVENT, handler);
    return () => window.removeEventListener(STORAGE_QUOTA_EVENT, handler);
  }, []);

  const handleRestore = (index: number) => {
    restoreVersion(index, onHtmlChange, onCssChange, onJsChange);
  };

  const { colors } = useSandboxTheme();

  // Minimal color scheme - always grayed out except error
  const dotColor = status === 'error' ? 'red.400' : colors.textMuted;
  const textColor = status === 'error' ? 'red.400' : colors.textMuted;

  const statusText = {
    saved: 'Saved',
    saving: 'Saving',
    error: 'Error',
  }[status];

  return (
    <Menu placement="top">
      <MenuButton
        cursor="pointer"
        transition="all 0.15s"
        _hover={{ opacity: 0.8 }}
        px={2}
        py={1}
        borderRadius="md"
      >
        <HStack spacing={1.5}>
          <Box
            w="5px"
            h="5px"
            borderRadius="full"
            bg={dotColor}
            transition="background 0.2s"
          />
          <Text
            fontSize="11px"
            color={textColor}
            fontWeight="400"
            letterSpacing="0.01em"
            transition="color 0.2s"
          >
            {statusText}
            {versions.length > 0 && ` (${versions.length})`}
          </Text>
        </HStack>
      </MenuButton>
      {versions.length > 0 && (
        <Portal>
          <MenuList
            bg={colors.popoverBg}
            borderColor={colors.popoverBorder}
            minW="180px"
            boxShadow={colors.popoverShadow}
            py={2}
            zIndex="popover"
          >
            <Text
              fontSize="10px"
              color={colors.sectionLabel}
              fontWeight="600"
              letterSpacing="0.05em"
              textTransform="uppercase"
              px={3}
              py={2}
            >
              Restore Previous Version
            </Text>
            {versions.map((version: CodeVersion, index: number) => (
              <MenuItem
                key={version.timestamp}
                onClick={() => handleRestore(index)}
                bg="transparent"
                _hover={{ bg: colors.hoverBg }}
                fontSize="xs"
                color={colors.textSecondary}
                py={2}
              >
                {getRelativeTime(version.timestamp)}
              </MenuItem>
            ))}
          </MenuList>
        </Portal>
      )}
    </Menu>
  );
};

export default SaveIndicator;
